/**
 * Auth context — Google OAuth via HttpOnly JWT cookie + RBAC permissions.
 *
 * Provides:
 *   • user / setUser
 *   • loading
 *   • googleConfig (client_id, auth_endpoint, scopes)
 *   • login() — kicks off Google OAuth
 *   • logout()
 *   • checkAuth() — re-hydrates from /api/auth/me + /api/users/me/permissions
 *   • permissions, rbacRole (raw)
 *   • hasPermission / canRead / canWrite / canDelete
 *   • isOwner, isAdminOrOwner
 *
 * `canRead` is permissive when RBAC hasn't loaded yet so the sidebar
 * doesn't go blank on first render — the backend still enforces.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { API_URL } from '../lib/constants';

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [googleConfig, setGoogleConfig] = useState(null);
  const [permissions, setPermissions] = useState({}); // resource_key → level
  const [rbacRole, setRbacRole] = useState(null);     // owner | admin | user

  // Fetch Google OAuth config from backend on mount
  useEffect(() => {
    const fetchGoogleConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/google/config`);
        if (response.ok) {
          const config = await response.json();
          setGoogleConfig(config);
        }
      } catch (error) {
        console.error('Failed to fetch Google config:', error);
      }
    };
    fetchGoogleConfig();
  }, []);

  const checkAuth = useCallback(async () => {
    // Fetch current user + RBAC permissions. Works on every route —
    // including /auth/callback where the OAuth handler explicitly calls
    // it after the cookie is in place.
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include',
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);

        try {
          const permRes = await fetch(`${API_URL}/api/users/me/permissions`, {
            credentials: 'include',
          });
          if (permRes.ok) {
            const pData = await permRes.json();
            setPermissions(pData.permissions || {});
            setRbacRole(pData.role || null);
          }
        } catch (permErr) {
          console.warn('Could not fetch permissions:', permErr);
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    if (!googleConfig) {
      console.error('Google OAuth config not loaded yet');
      return;
    }
    const params = new URLSearchParams({
      client_id: googleConfig.client_id,
      redirect_uri: window.location.origin + '/auth/callback',
      response_type: 'code',
      scope: googleConfig.scopes.join(' '),
      access_type: 'offline',
      prompt: 'select_account',
    });
    window.location.href = `${googleConfig.auth_endpoint}?${params.toString()}`;
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    document.cookie = 'session_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    setUser(null);
    window.location.href = '/';
  };

  // Permission-level ordering used by all the RBAC helpers below.
  const ORDER = { none: 0, read: 1, write: 2, delete: 3 };

  // While the permissions endpoint hasn't resolved we stay permissive
  // so the sidebar doesn't go blank — the backend still enforces auth.
  const rbacLoaded = !!rbacRole && Object.keys(permissions).length > 0;

  const hasPermission = (resource, required = 'read') => {
    if (!resource) return true;
    if (!rbacLoaded) return true;
    return (ORDER[permissions[resource]] ?? 0) >= (ORDER[required] ?? 0);
  };

  const canRead = (r) => hasPermission(r, 'read');
  const canWrite = (r) => hasPermission(r, 'write');
  const canDelete = (r) => hasPermission(r, 'delete');

  return (
    <AuthContext.Provider
      value={{
        user, setUser, loading, login, logout, checkAuth, googleConfig,
        permissions, rbacRole, hasPermission, canRead, canWrite, canDelete,
        isOwner: rbacRole === 'owner',
        isAdminOrOwner: rbacRole === 'owner' || rbacRole === 'admin',
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
